import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    createInvite,
    CreateInviteData,
    deactivateUser,
    DeactivateUserData,
    getClientPreferences,
    GetClientPreferencesData,
    getClients,
    GetClientsData,
    modifyClientPreferences,
    ModifyClientPreferencesData,
    updateUser,
    UpdateUserData,
} from "@/api";
import { appState, UserPreference } from "@/app/appState";
import { Request } from "@/components/CRUD";
import { ActionHandler } from "@/components/UI/tools";
import { Add_new, Remove, Save } from "@/components/UI/svgs";
import { ACCESS_LEVELS, accessLabel } from "./tree";
import { EmptyRow, Field, Pill, Row, Section, SelectField, TextField } from "./ui";

type person = {
    id: number;
    full_name: string;
    email: string;
    status: string;
    permission_type: number;
    relationship_type: string;
    user_lock: number | null;
};

const Preferences = observer(({ personId }: { personId: number }) => {
    const [preferences, setPreferences] = useState<UserPreference[]>([]);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState<{ id?: number; content: string }>();

    const load = Request<GetClientPreferencesData, any>(
        getClientPreferences,
        () => ({ body: { id: personId } }),
        setLoading,
        (data: any) => setPreferences(data.preferences ?? []),
        () => setPreferences([]),
        () => personId > 0,
    );

    const modify = Request<ModifyClientPreferencesData, any, { id?: number; content?: string }>(
        modifyClientPreferences,
        (props) => ({
            body: { client_id: personId, preference_id: props.id, content: props.content },
        }),
        setLoading,
        () => {
            setDraft(undefined);
            load();
        },
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("settings.people.errorSavePref"),
            }),
        () => true,
    );

    useEffect(() => {
        load();
    }, [personId]);

    return (
        <div className="flex flex-col gap-[.35rem]">
            <div className="flex flex-row items-center justify-between">
                <small className="caption">{appState.t("settings.people.preferences")}</small>
                <ActionHandler
                    title={appState.t("settings.people.addPreference")}
                    funq={() => setDraft({ content: "" })}
                    disabled={loading || draft !== undefined}
                >
                    <Add_new size={14} />
                </ActionHandler>
            </div>

            {preferences.length === 0 && !draft && (
                <p className="detail-text">{appState.t("settings.people.noPreferences")}</p>
            )}

            {preferences.map((preference) =>
                draft?.id === preference.id ? null : (
                    <div key={preference.id} className="flex flex-row items-start gap-[.35rem]">
                        <p
                            className="editable-text flex-grow"
                            onClick={() => setDraft({ id: preference.id, content: preference.content })}
                        >
                            {preference.content}
                        </p>
                        <ActionHandler
                            title={appState.t("settings.people.deletePreference")}
                            funq={() =>
                                appState.setModal({
                                    title: appState.t("settings.people.deletePreferenceConfirm"),
                                    body: preference.content,
                                    action: () => modify({ id: preference.id }),
                                })
                            }
                            disabled={loading}
                        >
                            <Remove size={14} />
                        </ActionHandler>
                    </div>
                ),
            )}

            {draft && (
                <div className="flex flex-col gap-[.25rem]">
                    <textarea
                        autoFocus
                        value={draft.content}
                        onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                        className="textarea"
                        placeholder={appState.t("settings.people.preferencePlaceholder")}
                    />
                    <div className="flex flex-row justify-end gap-[.25rem]">
                        <ActionHandler title={appState.t("common.cancel")} funq={() => setDraft(undefined)} disabled={loading}>
                            {appState.t("common.cancel")}
                        </ActionHandler>
                        <ActionHandler
                            title={appState.t("settings.people.savePreference")}
                            funq={() => modify({ id: draft.id, content: draft.content })}
                            disabled={loading || draft.content.trim().length === 0}
                        >
                            <Save size={14} />
                        </ActionHandler>
                    </div>
                </div>
            )}
        </div>
    );
});

const PersonEditor = observer(({
    person,
    orgId,
    onDone,
}: {
    person: person;
    orgId: number;
    onDone: () => void;
}) => {
    const [fullName, setFullName] = useState(person.full_name);
    const [permissionType, setPermissionType] = useState<number | undefined>(person.permission_type);
    const [loading, setLoading] = useState(false);

    const save = Request<UpdateUserData, any>(
        updateUser,
        () => ({
            body: {
                id: person.id,
                full_name: fullName,
                permission_type: permissionType!,
                organisation_id: orgId,
            },
        }),
        setLoading,
        () => onDone(),
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("settings.people.errorUpdate"),
            }),
        () => fullName.trim().length > 0 && permissionType !== undefined,
    );

    const remove = Request<DeactivateUserData, any>(
        deactivateUser,
        () => ({ body: { id: person.id, organisation_id: orgId } }),
        setLoading,
        () => onDone(),
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("settings.people.errorDeactivate"),
            }),
        () => true,
    );

    return (
        <div className="row-form row-form-active">
            <div className="flex flex-row gap-[.5rem] flex-wrap">
                <Field label={appState.t("common.name")}>
                    <TextField value={fullName} onChange={setFullName} autoFocus />
                </Field>
                <Field label={appState.t("objects.access")}>
                    <SelectField
                        value={permissionType}
                        onChange={setPermissionType}
                        options={ACCESS_LEVELS}
                    />
                </Field>
            </div>

            <Preferences personId={person.id} />

            <div className="flex flex-row justify-between items-center">
                <ActionHandler
                    title={appState.t("settings.people.removeFromOrg")}
                    funq={() =>
                        appState.setModal({
                            title: appState.t("settings.people.deactivatePerson"),
                            body: appState.t("settings.people.deactivateBody", { name: person.full_name }),
                            action: () => remove(),
                        })
                    }
                    disabled={loading}
                    className="btn-danger"
                >
                    {appState.t("common.deactivate")}
                </ActionHandler>
                <div className="flex flex-row gap-[.25rem]">
                    <ActionHandler title={appState.t("common.cancel")} funq={onDone} disabled={loading}>
                        {appState.t("common.cancel")}
                    </ActionHandler>
                    <ActionHandler
                        title={appState.t("common.save")}
                        funq={() => save()}
                        disabled={loading || fullName.trim().length === 0}
                    >
                        <Save size={16} />
                    </ActionHandler>
                </div>
            </div>
        </div>
    );
});

const InviteForm = observer(({ orgId, onDone }: { orgId: number; onDone: () => void }) => {
    const [email, setEmail] = useState("");
    const [permissionType, setPermissionType] = useState<number | undefined>(7);
    const [loading, setLoading] = useState(false);

    const invite = Request<CreateInviteData, any>(
        createInvite,
        () => ({
            body: {
                permission_type: permissionType!,
                email: email.trim().length > 0 ? email : undefined,
                organisation_id: orgId,
            },
        }),
        setLoading,
        (data: any) => {
            onDone();
            appState.setModal({
                title: appState.t("settings.people.inviteCreated"),
                body: appState.t("settings.people.shareLink"),
                comment: { name: appState.t("common.link"), val: window.location.origin + data.invite_link },
            });
        },
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("settings.people.errorInvite"),
            }),
        () => permissionType !== undefined,
    );

    return (
        <div className="row-form row-form-active">
            <div className="flex flex-row gap-[.5rem] flex-wrap">
                <Field label={appState.t("settings.people.emailOptional")}>
                    <TextField value={email} onChange={setEmail} type="email" autoFocus />
                </Field>
                <Field label={appState.t("objects.access")}>
                    <SelectField value={permissionType} onChange={setPermissionType} options={ACCESS_LEVELS} />
                </Field>
            </div>
            <div className="flex flex-row justify-end gap-[.25rem]">
                <ActionHandler title={appState.t("common.cancel")} funq={onDone} disabled={loading}>
                    {appState.t("common.cancel")}
                </ActionHandler>
                <ActionHandler title={appState.t("settings.people.generateLink")} funq={() => invite()} disabled={loading}>
                    {appState.t("settings.people.generateLink")}
                </ActionHandler>
            </div>
        </div>
    );
});

export const PeopleSection = observer(({
    orgId,
    canManage,
}: {
    orgId: number;
    canManage: boolean;
}) => {
    const [people, setPeople] = useState<person[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState<number>();
    const [inviting, setInviting] = useState(false);

    const load = Request<GetClientsData, any>(
        getClients,
        () => ({ body: { page_size: 999999, page_number: 0, organisation_id: orgId } }),
        setLoading,
        (data: any) => setPeople(data.users ?? []),
        () => setPeople([]),
        () => orgId > 0,
    );

    useEffect(() => {
        setEditingId(undefined);
        setInviting(false);
        setPeople([]);
        load();
    }, [orgId]);

    const closeAndReload = () => {
        setEditingId(undefined);
        setInviting(false);
        load();
    };

    return (
        <Section
            title={appState.t("settings.people.title")}
            hint={people.length > 0 ? `${people.length}` : undefined}
            action={
                canManage ? (
                    <ActionHandler
                        title={appState.t("settings.people.createInvite")}
                        funq={() => setInviting((p) => !p)}
                        selected={inviting}
                    >
                        {appState.t("settings.people.invite")}
                    </ActionHandler>
                ) : undefined
            }
        >
            {inviting && <InviteForm orgId={orgId} onDone={closeAndReload} />}

            {people.length === 0 && !loading && !inviting && (
                <EmptyRow>{appState.t("settings.people.empty")}</EmptyRow>
            )}
            {people.length === 0 && loading && <EmptyRow>{appState.t("common.loading")}</EmptyRow>}

            {people.map((person) =>
                editingId === person.id ? (
                    <PersonEditor
                        key={person.id}
                        person={person}
                        orgId={orgId}
                        onDone={closeAndReload}
                    />
                ) : (
                    <Row
                        key={person.id}
                        onClick={
                            canManage && person.relationship_type === "same_level"
                                ? () => setEditingId(person.id)
                                : undefined
                        }
                        chevron={canManage && person.relationship_type === "same_level"}
                        trailing={
                            <div className="flex flex-row gap-[.25rem] items-center">
                                {person.relationship_type !== "same_level" && (
                                    <Pill title={appState.t("settings.people.inheritedTitle")}>{appState.t("common.inherited")}</Pill>
                                )}
                                <Pill tone={person.permission_type >= 4 ? "accent" : "neutral"}>
                                    {accessLabel(person.permission_type)}
                                </Pill>
                            </div>
                        }
                    >
                        <p className="l truncate">{person.full_name}</p>
                        <p className="detail-text truncate">{person.email}</p>
                    </Row>
                ),
            )}
        </Section>
    );
});
